import { z } from 'zod';

export const roastStageSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, 'Stage name is required'),
  temperature: z.number().min(0, 'Temperature must be positive'),
  time: z.number().min(0, 'Time must be positive'), // in seconds or minutes
  description: z.string().optional(),
});

export const roastProfilePointSchema = z.object({
  time: z.string(),
  seconds: z.number(),
  bean: z.number().nullable(),
  fire: z.number().nullable(),
  airflow: z.number().nullable(),
  ror: z.number().nullable(),
  airTemp: z.number().nullable().optional(),
  drumTemp: z.number().nullable().optional(),
  environmentTemp: z.number().nullable().optional(),
  drumRpm: z.number().nullable().optional(),
});

export const recipeSchema = z.object({
  name: z.string().min(1, 'Recipe name is required'),
  description: z.string().optional(),
  
  // Weights
  targetBeanWeight: z.number().min(0, 'Target Bean Weight must be positive'), // Input Weight
  targetWeightOutput: z.number().optional(), // Output Weight
  expectedShrinkage: z.number().optional(),

  // Roast Info
  machine: z.string().optional(),
  machineTypeId: z.string().optional(),
  roastType: z.string().optional(), // 'sample' | 'filter' | 'omni' | 'espresso'
  
  // Agtron
  agtron: z.object({
    wholeBean: z.number().optional(),
    groundBean: z.number().optional(),
    classification: z.object({
      scaa: z.string(),
      common: z.string()
    }).optional()
  }).optional(),

  // Profile Data
  profile: z.array(roastProfilePointSchema).optional(),
  
  // Stage Summary (Start, Turning Point, etc.)
  stageSummary: z
    .record(
      z.string(),
      z.object({
        time: z.string(),
        temp: z.union([z.number(), z.string()]),
      }),
    )
    .optional(),

  // General
  totalTime: z.number().min(0, 'Total Time must be positive'),
  shelfLife: z.number().min(0, 'Shelf Life must be positive').optional().or(z.nan().transform(() => undefined)),
  internalNotes: z.string().optional(),
  stages: z.array(roastStageSchema).optional(), // Keeping for backward compatibility or future use
  tastingNotes: z.object({
    acidity: z.number().min(0).max(10),
    aftertaste: z.number().min(0).max(10),
    cleanCup: z.number().min(0).max(10),
    head: z.number().min(0).max(10),
    fragrance: z.number().min(0).max(10),
    sweetness: z.number().min(0).max(10),
    balance: z.number().min(0).max(10),
    aroma: z.number().min(0).max(10),
    body: z.number().min(0).max(10),
    flavorTags: z.array(z.string()),
    notes: z.string().optional(),
  }).optional(),
  
  // Inventory
  itemId: z.string().optional(), // Selected Green Bean ID
  itemName: z.string().optional(),
  itemCode: z.string().optional(),
});

export type RecipeFormData = z.infer<typeof recipeSchema>;

export interface TastingNotes {
  acidity: number;
  aftertaste: number;
  cleanCup: number;
  head: number;
  fragrance: number;
  sweetness: number;
  balance: number;
  aroma: number;
  body: number;
  flavorTags: string[];
  notes?: string;
}

export const AVAILABLE_FLAVOR_TAGS = [
  'tea-like', 'floral', 'jasmine', 'rose',
  'citrus', 'bergamot', 'lemon', 'lime', 'orange', 'grapefruit',
  'berry', 'strawberry', 'blueberry', 'raspberry', 'cherry',
  'stone-fruit', 'peach', 'apricot', 'tropical-fruit', 'pineapple', 'mango',
  'dried-fruit', 'raisin', 'date',
  'caramel', 'honey', 'brown-sugar', 'molasses', 'vanilla', 'maple', 'syrupy',
  'chocolate', 'dark-chocolate', 'milk-chocolate', 'cocoa', 'almond', 'hazelnut', 'walnut', 'peanut',
  'cinnamon', 'clove', 'nutmeg', 'anise',
  'earthy', 'cedar', 'tobacco', 'herbal', 'grassy',
  'malty', 'buttery', 'winey', 'bright', 'tart', 'savory'
];

export interface Recipe extends RecipeFormData {
  id: string;
  version: string;
  isActive: boolean;
  createdAt: string;
}
